import { ErrorMessage } from '@hookform/error-message';
import { Button, CardHeader, Label } from '@repo/shadcn';
import { Controller } from 'react-hook-form';
import { PiPlusCircleDuotone } from 'react-icons/pi';

import { InfoIcon } from '@assets/svg.icons/icons.tsx';

import { MODAL_ROUTE } from '@/constants';
import { useModal } from '@/hooks';
import { DynamicControl } from '@/shared/dynamic-control.tsx';

import { createProduct } from '../utils/product-inputs';
import { FieldName } from '../utils/product.types';
import { StrengthAutoComplete } from './strength-autocomplete';
import { TypeAutoComplete } from './type-autocomplete';
import { UnitAutoComplete } from './unit-autocomplete';

function CreateProductVariation({
    fields,
    append,
    remove,
    onClickFormActionBtn,
    isValid,
    control,

    fieldOptionsForPricing,
}: any) {
    const { openModal } = useModal();

    return (
        <div>
            <CardHeader>
                <div className=" flex items-center text-[18px] font-semibold text-gray-600">
                    <InfoIcon />
                    {createProduct.productPricing.title}
                </div>
            </CardHeader>
            {fields.map((field: any, index: number) => (
                <div
                    key={field.id}
                    className="mb-5 rounded-lg border bg-gray-100 p-5"
                >
                    <div className="flex justify-end">
                        <Button
                            style={
                                fields.length < 2 && index != 1
                                    ? { display: 'none' }
                                    : { display: 'block' }
                            }
                            variant="destructive"
                            onClick={() => remove(index)}
                        >
                            Remove
                        </Button>
                    </div>
                    <div className="mt-3 grid grid-cols-2 gap-5 lg:grid-cols-3">
                        {createProduct.productPricing.fields.map((product) => (
                            <div key={product.fieldName} className="mt-3">
                                <div className="item-center mb-2 flex h-fit justify-between">
                                    <Label className="h-fit">
                                        {product.label}
                                    </Label>
                                    {product.actionBtn && (
                                        <Label
                                            onClick={() =>
                                                onClickFormActionBtn(
                                                    product.actionBtnLabel,
                                                )
                                            }
                                            className="flex cursor-pointer items-center gap-1 text-primary hover:underline"
                                        >
                                            <PiPlusCircleDuotone
                                                size={16}
                                                className="-mt-1"
                                            />
                                            {product.actionBtnLabel}
                                        </Label>
                                    )}
                                </div>

                                <DynamicControl
                                    inputType={product.inputType}
                                    fieldName={
                                        `variations.${index}.${product.fieldName}` as FieldName
                                    }
                                    label={product.label}
                                    options={
                                        fieldOptionsForPricing[
                                            product.fieldName
                                        ]
                                    }
                                    control={control}
                                />
                                <ErrorMessage
                                    name={
                                        `variations.${index}.${product.fieldName}` as FieldName
                                    }
                                    render={({ message }) => (
                                        <p className="mt-1 text-sm text-red-600">
                                            {message}
                                        </p>
                                    )}
                                />
                            </div>
                        ))}
                        <Controller
                            name={'type_id'}
                            control={control}
                            render={() => (
                                <div className=" space-y-2">
                                    <div className=" flex justify-between">
                                        <Label> Type Name</Label>
                                        <Label
                                            onClick={() =>
                                                openModal(
                                                    MODAL_ROUTE.CREATE_PRODUCT_TYPE,
                                                )
                                            }
                                            className="flex cursor-pointer items-center gap-1 text-primary hover:underline"
                                        >
                                            <PiPlusCircleDuotone
                                                size={16}
                                                className="-mt-1"
                                            />
                                            Add type
                                        </Label>
                                    </div>
                                    <TypeAutoComplete
                                        control={control}
                                        name={
                                            `variations.${index}.type_id` as FieldName
                                        }
                                    />
                                    <ErrorMessage
                                        name={
                                            `variations.${index}.type_id` as FieldName
                                        }
                                        render={({ message }) => (
                                            <p className="text-sm text-red-600 ">
                                                {message}
                                            </p>
                                        )}
                                    />
                                </div>
                            )}
                        />
                        <Controller
                            name={'unit_id'}
                            control={control}
                            render={() => (
                                <div className=" space-y-2">
                                    <div className=" flex justify-between">
                                        <Label> Unit Name</Label>
                                        <Label
                                            onClick={() =>
                                                openModal(
                                                    MODAL_ROUTE.CREATE_PRODUCT_UNIT,
                                                )
                                            }
                                            className="flex cursor-pointer items-center gap-1 text-primary hover:underline"
                                        >
                                            <PiPlusCircleDuotone
                                                size={16}
                                                className="-mt-1"
                                            />
                                            Add unit
                                        </Label>
                                    </div>
                                    <UnitAutoComplete
                                        control={control}
                                        name={
                                            `variations.${index}.unit_id` as FieldName
                                        }
                                    />
                                    <ErrorMessage
                                        name={
                                            `variations.${index}.unit_id` as FieldName
                                        }
                                        render={({ message }) => (
                                            <p className="text-sm text-red-600 ">
                                                {message}
                                            </p>
                                        )}
                                    />
                                </div>
                            )}
                        />
                        <Controller
                            name={'strength_id'}
                            control={control}
                            render={() => (
                                <div className=" space-y-2">
                                    <div className=" flex justify-between">
                                        <Label> Strength Name</Label>
                                        <Label
                                            onClick={() =>
                                                openModal(
                                                    MODAL_ROUTE.CREATE_PRODUCT_STRENGTH,
                                                )
                                            }
                                            className="flex cursor-pointer items-center gap-1 text-primary hover:underline"
                                        >
                                            <PiPlusCircleDuotone
                                                size={16}
                                                className="-mt-1"
                                            />
                                            Add strength
                                        </Label>
                                    </div>
                                    <StrengthAutoComplete
                                        control={control}
                                        name={
                                            `variations.${index}.strength_id` as FieldName
                                        }
                                    />
                                    <ErrorMessage
                                        name={
                                            `variations.${index}.strength_id` as FieldName
                                        }
                                        render={({ message }) => (
                                            <p className="text-sm text-red-600 ">
                                                {message}
                                            </p>
                                        )}
                                    />
                                </div>
                            )}
                        />
                    </div>
                </div>
            ))}

            <Button
                disabled={!isValid}
                onClick={() =>
                    append({
                        type_id: '',
                        unit_id: '',
                        strength_id: '',
                        purchase_price: 0,
                        wholesale_price: 0,
                        retail_price: 0,
                    })
                }
            >
                Add Variation
            </Button>
        </div>
    );
}

export default CreateProductVariation;
